
import { UnitInputValue } from "@/types/unit";
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import MarginPaddingInputDragBar from "@/oxygen/classes/inputs/spacing/MarginPaddingInputDragBar.vue";
import { EasyEvent } from "@/util/easy-event";
import { OxygenControlsTreeContext } from "@/oxygen/classes/lib/types";
import { uiStore } from "@/store/store";

type MarginPaddingInputValue = {
  margin?: {
    top?: UnitInputValue;
    left?: UnitInputValue;
    right?: UnitInputValue;
    bottom?: UnitInputValue;
  };
  padding?: {
    top?: UnitInputValue;
    left?: UnitInputValue;
    right?: UnitInputValue;
    bottom?: UnitInputValue;
  };
};

@Component({
  components: { MarginPaddingInputDragBar }
})
export default class OxygenSpacingInput extends Vue {
  @Inject({ from: "oxyContext" })
  readonly context!: OxygenControlsTreeContext;

  @Prop()
  value?: MarginPaddingInputValue;

  @Prop()
  path!: string;

  easyEvent = new EasyEvent();

  modifierKey: "shift" | "alt" | null = null;
  hoveredType: "margin" | "padding" | null = null;
  hoveredSide: "top" | "left" | "right" | "bottom" | null = null;

  dragging = false;
  draggingType: "margin" | "padding" | null = null;

  updateProperty(
    type: "margin" | "padding",
    side: "top" | "left" | "right" | "bottom",
    e: {
      value: UnitInputValue;
      modifierKey?: "shift" | "alt";
      transactionKey?: string;
    }
  ) {
    let newValue;
    const sideValue = e.value;

    if (type === "padding" && sideValue && Number(sideValue.number) < 0) {
      sideValue.number = 0;
      sideValue.style = `${sideValue.number}${sideValue.unit}`;
    }

    if (!e.modifierKey) {
      newValue = {
        ...this.value,
        [type]: {
          ...this.value?.[type],
          [side]: sideValue
        }
      };
    } else if (e.modifierKey === "alt") {
      const oppositeSide = getOppositeSide(side);
      newValue = {
        ...this.value,
        [type]: {
          ...this.value?.[type],
          [side]: sideValue,
          [oppositeSide]: sideValue
        }
      };
    } else if (e.modifierKey === "shift") {
      newValue = {
        ...this.value,
        [type]: {
          ...this.value?.[type],
          left: sideValue,
          right: sideValue,
          top: sideValue,
          bottom: sideValue
        }
      };
    }

    this.context.setControlValueAt(
      this.path,
      uiStore.currentBreakpoint.id,
      newValue,
      e?.transactionKey
    );
  }

  isSideActive(
    type: "margin" | "padding",
    side: "top" | "left" | "right" | "bottom"
  ) {
    if (this.modifierKey === "shift" && this.hoveredType === type) {
      return true;
    }

    if (this.modifierKey === "alt" && this.hoveredType === type) {
      const isVerticalSide = (side: string | null) =>
        side === "top" || side === "bottom";
      const isHorizontalSide = (side: string | null) =>
        side === "left" || side === "right";

      if (isVerticalSide(this.hoveredSide)) {
        return isVerticalSide(side);
      }

      if (isHorizontalSide(this.hoveredSide)) {
        return isHorizontalSide(side);
      }
    }

    return false;
  }

  setHoveredType(
    type: "margin" | "padding",
    side?: "top" | "left" | "right" | "bottom"
  ) {
    this.hoveredType = type;

    if (side) {
      this.hoveredSide = side;
    }
  }

  setDragging(dragging: boolean, type: "margin" | "padding") {
    if (dragging) {
      this.dragging = true;
      this.draggingType = type;
    } else {
      this.dragging = false;
      this.draggingType = null;
    }
  }

  checkMetaKeys(event: KeyboardEvent | MouseEvent) {
    if (event.altKey) {
      this.modifierKey = "alt";
    } else if (event.shiftKey) {
      this.modifierKey = "shift";
    } else {
      this.modifierKey = null;
    }
  }

  onMouseEnter(event: MouseEvent) {
    this.checkMetaKeys(event);

    this.easyEvent.on("keyup", this.checkMetaKeys);
    this.easyEvent.on("keydown", this.checkMetaKeys);
  }

  onMouseLeave() {
    if (!this.dragging) {
      this.hoveredType = null;
      this.hoveredSide = null;

      this.easyEvent.off("keyup", this.checkMetaKeys);
      this.easyEvent.off("keydown", this.checkMetaKeys);
    }
  }

  beforeDestroy() {
    const container = this.$refs.container as HTMLElement;
    container.removeEventListener("mouseenter", this.onMouseEnter);
    container.removeEventListener("mouseleave", this.onMouseLeave);

    this.easyEvent.off("keyup", this.checkMetaKeys);
    this.easyEvent.off("keydown", this.checkMetaKeys);
  }
}

function getOppositeSide(side: "top" | "left" | "right" | "bottom") {
  if (side === "top") {
    return "bottom";
  } else if (side === "left") {
    return "right";
  } else if (side === "right") {
    return "left";
  } else {
    return "top";
  }
}
